import React, { useCallback, useState } from 'react';
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import {
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
} from '@mui/x-data-grid';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

interface FilterCondition {
  id: string;
  columnField: string;
  operatorValue: string;
  value?: string;
}

interface MultiFilterBuilderProps {
  columns: { field: string; headerName: string }[];
  filterModelRef: React.MutableRefObject<GridFilterModel>;
  onFilterModelChange: (filterModel: GridFilterModel) => void;
}

const TEXT_OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'isNullOrMissing', label: 'Is empty' },
];

export function MultiFilterBuilder({
  columns,
  filterModelRef,
  onFilterModelChange,
}: MultiFilterBuilderProps) {
  // Helper to determine if an operator requires a value input
  const operatorRequiresValue = useCallback((op: string) => {
    return op !== 'isNullOrMissing';
  }, []);

  // Define the shape of a new blank filter row
  const makeBlankRow = useCallback((): FilterCondition => {
    return {
      id: uuidv4(),
      columnField: columns[0]?.field ?? '',
      operatorValue: 'contains',
      value: '',
    };
  }, [columns]);

  /**
   * Initialize local filter conditions from the parent model exactly once.
   * After that, this component's local state is the "source of truth."
   */
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>(
    () => {
      if (
        !filterModelRef.current.items ||
        filterModelRef.current.items.length === 0
      ) {
        return [makeBlankRow()];
      }
      return filterModelRef.current.items.map(item => ({
        id: item.id?.toString() ?? uuidv4(),
        columnField: item.field,
        operatorValue: item.operator,
        value: item.value as string,
      }));
    }
  );

  // Track logic operator; default to AND if the parent wasn't 'or'
  const [logicOperator, setLogicOperator] = useState<GridLogicOperator>(() =>
    filterModelRef.current.logicOperator === 'or'
      ? GridLogicOperator.Or
      : GridLogicOperator.And
  );

  /**
   * Convert our local FilterConditions to a GridFilterModel and call onFilterModelChange.
   */
  const pushFilterModel = useCallback(
    (conditions: FilterCondition[], operator: GridLogicOperator) => {
      const valid = conditions.filter(c =>
        operatorRequiresValue(c.operatorValue) ? c.value?.trim() !== '' : true
      );
      const items: GridFilterItem[] = valid.map(c => ({
        id: c.id,
        field: c.columnField,
        operator: c.operatorValue,
        value: operatorRequiresValue(c.operatorValue) ? c.value : undefined,
      }));
      onFilterModelChange({ items, logicOperator: operator });
    },
    [onFilterModelChange, operatorRequiresValue]
  );

  /**
   * Handle changes to the logic operator.
   */
  const handleLogicOperatorChange = useCallback(
    (newOp: GridLogicOperator) => {
      setLogicOperator(newOp);
      // If there's at least one valid row, push updated operator upward.
      if (
        filterConditions.some(fc =>
          operatorRequiresValue(fc.operatorValue)
            ? fc.value?.trim() !== ''
            : true
        )
      ) {
        pushFilterModel(filterConditions, newOp);
      }
    },
    [filterConditions, pushFilterModel, operatorRequiresValue]
  );

  /**
   * Handle user changes in an individual filter row's column/operator/value.
   */
  const handleConditionChange = useCallback(
    (id: string, field: keyof FilterCondition, newValue: string) => {
      setFilterConditions(prev => {
        const next = prev.map(cond => {
          if (cond.id !== id) return cond;
          const updated: FilterCondition = {
            ...cond,
            [field]: newValue,
          } as FilterCondition;
          // If operator changed to value-less, clear value
          if (field === 'operatorValue' && !operatorRequiresValue(newValue)) {
            updated.value = '';
          }
          return updated;
        });

        // Determine validity before and after for push conditions
        const prevCond = prev.find(cond => cond.id === id);
        const wasValid = prevCond
          ? operatorRequiresValue(prevCond.operatorValue)
            ? prevCond.value?.trim() !== ''
            : true
          : false;
        const newCond = next.find(cond => cond.id === id)!;
        const isValid = operatorRequiresValue(newCond.operatorValue)
          ? newCond.value?.trim() !== ''
          : true;

        if (wasValid || isValid) {
          pushFilterModel(next, logicOperator);
        }
        return next;
      });
    },
    [logicOperator, pushFilterModel, operatorRequiresValue]
  );

  /**
   * Add a new blank filter row.
   */
  const handleAddFilter = useCallback(() => {
    if (!columns.length) return;
    setFilterConditions(prev => [...prev, makeBlankRow()]);
  }, [columns, makeBlankRow]);

  /**
   * Remove an existing filter row.
   */
  const handleRemoveFilter = useCallback(
    (id: string) => {
      setFilterConditions(prev => {
        const removed = prev.find(cond => cond.id === id);
        const next = prev.filter(cond => cond.id !== id);

        // If we removed a row that was valid, push new model upward
        const removedWasValid = removed
          ? operatorRequiresValue(removed.operatorValue)
            ? removed.value?.trim()
            : true
          : false;
        if (removedWasValid) {
          pushFilterModel(next, logicOperator);
        }

        // If none remain, add one blank row for convenience
        if (next.length === 0) {
          return [makeBlankRow()];
        }
        return next;
      });
    },
    [logicOperator, pushFilterModel, makeBlankRow, operatorRequiresValue]
  );

  /**
   * Reset everything to a single blank row. Then push up an empty filter model.
   */
  const handleResetFilters = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const blank = makeBlankRow();
      setFilterConditions([blank]);
      // Let the parent know we have no real filters (items: [])
      onFilterModelChange({
        items: [],
        logicOperator: GridLogicOperator.And,
      });
    },
    [makeBlankRow, onFilterModelChange]
  );

  return (
    <Box>
      {/* Logic Operator Dropdown */}
      <FormControl size="small" sx={{ mb: 1, minWidth: 120 }}>
        <InputLabel id="logic-operator-label">Logic</InputLabel>
        <Select
          labelId="logic-operator-label"
          label="Logic"
          value={logicOperator}
          onChange={e =>
            handleLogicOperatorChange(e.target.value as GridLogicOperator)
          }
        >
          <MenuItem value={GridLogicOperator.And}>AND</MenuItem>
          <MenuItem value={GridLogicOperator.Or}>OR</MenuItem>
        </Select>
      </FormControl>

      {/* Filter Rows */}
      <Stack spacing={1}>
        {filterConditions.map((cond, index) => (
          <Paper
            key={cond.id}
            sx={{ p: 1.5, borderRadius: 1, display: 'flex', gap: 1 }}
          >
            {/* Column Picker */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Column</InputLabel>
              <Select
                label="Column"
                value={cond.columnField}
                onChange={e =>
                  handleConditionChange(cond.id, 'columnField', e.target.value)
                }
              >
                {columns.map(({ field, headerName }) => (
                  <MenuItem key={field} value={field}>
                    {headerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Operator Picker */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                label="Operator"
                value={cond.operatorValue}
                onChange={e =>
                  handleConditionChange(
                    cond.id,
                    'operatorValue',
                    e.target.value
                  )
                }
              >
                {TEXT_OPERATORS.map(op => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Value Input */}
            {operatorRequiresValue(cond.operatorValue) ? (
              <TextField
                size="small"
                label="Value"
                value={cond.value ?? ''}
                onChange={e =>
                  handleConditionChange(cond.id, 'value', e.target.value)
                }
                sx={{ width: 160 }}
                required
              />
            ) : (
              <TextField
                size="small"
                label="Value"
                value=""
                disabled
                sx={{ width: 160 }}
                placeholder="No value needed"
              />
            )}

            {/* Remove Filter Button */}
            <IconButton
              onClick={() => handleRemoveFilter(cond.id)}
              color="error"
              size="small"
              aria-label="Remove filter"
            >
              <IndeterminateCheckBoxIcon fontSize="small" />
            </IconButton>

            {/* Only show "Add" button on the last row (and if user typed something) */}
            {index === filterConditions.length - 1 && (
              <IconButton
                onClick={handleAddFilter}
                color="primary"
                size="small"
                aria-label="Add filter"
                disabled={
                  operatorRequiresValue(cond.operatorValue)
                    ? !cond.value?.trim()
                    : false
                }
              >
                <AddBoxIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        ))}
      </Stack>

      {/* Reset Link */}
      <Box mt={1}>
        <a href="#" onClick={handleResetFilters} style={{ fontSize: '0.9rem' }}>
          Reset filters
        </a>
      </Box>
    </Box>
  );
}
